'use strict';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Avatar,
	Box,
	Button,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import BarChart from '@mui/icons-material/BarChart';
import Speed from '@mui/icons-material/Speed';
import MenuBook from '@mui/icons-material/MenuBook';
import AutoStories from '@mui/icons-material/AutoStories';
import NotInterestedOutlined from '@mui/icons-material/NotInterestedOutlined';
import DatasetOutlined from '@mui/icons-material/DatasetOutlined';
import { Brand } from '@/lenses/uilenses/theme/colors';
import {
	PRODUCT_FAMILIES,
	THERAPEUTIC_AREAS,
	STUDY_GOALS,
	PRODUCT_FIT_INTRO,
	SELF_SERVICE_BASE,
	isFitComplete,
	getFitIncompleteReasons,
	getSampleCountInputError,
	isServicesStepComplete,
	isTherapeuticStepComplete,
	isGoalStepComplete,
	getInitialStudyBuilderSelections,
	COMMON_SPECIES_TILES,
	MATRIX_OPTIONS_BY_SPECIES,
	MATRIX_CATEGORIES,
	MATRIX_TYPES_BY_CATEGORY,
} from '../studyBuilder.config.js';
import { ChoiceLogoTiles } from './ChoiceLogoTiles.jsx';
import { ProductFinderDialog } from './ProductFinderDialog.jsx';
import { PRODUCT_FAMILY_ICONS } from '../studyBuilderProductIcons.jsx';
import { STUDY_GOAL_ICONS } from '../studyGoalIcons.jsx';

const SCOPE_FIELD_SX = {
	'& .MuiOutlinedInput-root': {
		minHeight: 48,
		borderRadius: 2,
		alignItems: 'center',
	},
	'& .MuiOutlinedInput-input': {
		paddingTop: '12.5px',
		paddingBottom: '12.5px',
	},
	'& .MuiSelect-select': {
		display: 'flex',
		alignItems: 'center',
		minHeight: '1.25rem',
	},
	'& .MuiInputLabel-root': { lineHeight: 1.25 },
};

const FALLBACK_SPECIES_GRADIENTS = [
	'linear-gradient(135deg, #284A6F 0%, #60759f 100%)',
	'linear-gradient(135deg, #855DA3 0%, #3987ef 100%)',
	'linear-gradient(135deg, #001D38 0%, #284A6F 100%)',
	'linear-gradient(135deg, #12824a 0%, #57db8e 100%)',
];

const FIT_STEPS = [
	{ id: 'product', title: 'What type of study are you running?', subtitle: 'Pick the product line that best fits your research.' },
	{ id: 'services', title: 'Do you need extra services?', subtitle: 'Statistics and interpretation are optional — our bioinformatics platform is always included.' },
	{ id: 'therapeutic', title: 'What area(s) is your research in?', subtitle: 'Select up to three therapeutic areas that describe your program.' },
	{ id: 'goal', title: 'What question are you trying to answer?', subtitle: 'This helps us recommend the right approach for your study.' },
	{
		id: 'scope',
		title: 'Tell us about your samples',
		subtitle: 'Let us know the species, matrix, and how many samples you plan to send.',
	},
];

/** Progressive section wrapper with scroll-into-view on first reveal. */
function FitSection({ id, visible, title, subtitle, children }) {
	const ref = useRef(null);
	const wasVisible = useRef(visible);
	const didMount = useRef(false);

	useEffect(() => {
		if (!didMount.current) {
			didMount.current = true;
			wasVisible.current = visible;
			return;
		}
		if (visible && !wasVisible.current) {
			const t = setTimeout(() => {
				ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 320);
			wasVisible.current = true;
			return () => clearTimeout(t);
		}
		wasVisible.current = visible;
	}, [visible]);

	if (!visible) return null;

	return (
		<Box
			ref={ref}
			data-testid={`fit-section-${id}`}
			sx={{
				mb: 7,
				scrollMarginTop: 32,
				animation: 'fitSectionIn 0.4s ease-out',
				'@keyframes fitSectionIn': {
					from: { opacity: 0, transform: 'translateY(12px)' },
					to: { opacity: 1, transform: 'translateY(0)' },
				},
			}}
		>
			<Typography
				variant="h5"
				component="h2"
				sx={{ fontWeight: 700, color: '#1e293b', mb: 1, textAlign: 'center', letterSpacing: '-0.01em', fontSize: '1.35rem' }}
			>
				{title}
			</Typography>
			<Typography
				variant="body2"
				sx={{ color: '#64748b', mb: 3.5, lineHeight: 1.7, maxWidth: 560, mx: 'auto', textAlign: 'center' }}
			>
				{subtitle}
			</Typography>
			{children}
		</Box>
	);
}


function ServicePickCard({ selected, title, body, icon, stripeGradient, onClick, testId }) {
	return (
		<Paper
			component="button"
			type="button"
			data-testid={testId}
			elevation={0}
			onClick={onClick}
			sx={{
				flex: '1 1 180px',
				minWidth: 180,
				maxWidth: 1,
				p: 0,
				cursor: 'pointer',
				textAlign: 'left',
				border: '2px solid',
			borderColor: selected ? '#374151' : '#e2e8f0',
			borderRadius: 3,
			background: selected ? '#374151' : '#fff',
			overflow: 'hidden',
			transition: 'all 0.2s ease',
			'&:hover': {
				borderColor: selected ? '#374151' : Brand.molecularBlue,
				boxShadow: selected ? 'none' : '0 4px 16px rgba(0, 29, 56, 0.08)',
				transform: selected ? 'none' : 'translateY(-1px)',
			},
		}}
	>
		<Box sx={{ p: 2.5, display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
			<Avatar
				sx={{
					width: 44,
					height: 44,
					background: selected ? 'rgba(255,255,255,0.2)' : stripeGradient,
					color: Brand.pureWhite,
				}}
			>
				{icon}
			</Avatar>
			<Box sx={{ minWidth: 0 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 700, color: selected ? Brand.pureWhite : '#1e293b', lineHeight: 1.25 }}>
						{title}
					</Typography>
					<Typography variant="body2" sx={{ color: selected ? 'rgba(255,255,255,0.8)' : '#64748b', mt: 0.5, lineHeight: 1.55 }}>
						{body}
					</Typography>
				</Box>
			</Box>
		</Paper>
	);
}

function TherapeuticAreaTileGrid({ selectedIds, onChange, max }) {
	const ids = selectedIds || [];
	const toggle = (id) => {
		if (ids.includes(id)) {
			onChange(ids.filter((x) => x !== id));
			return;
		}
		if (ids.length >= max) return;
		onChange([...ids, id]);
	};
	return (
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
			{THERAPEUTIC_AREAS.map((t) => {
				const selected = ids.includes(t.id);
				const disabled = !selected && ids.length >= max;
				return (
					<Button
						key={t.id}
						data-testid={`therapeutic-tile-${t.id}`}
						variant={selected ? 'contained' : 'outlined'}
						size="small"
						disabled={disabled}
						onClick={() => toggle(t.id)}
						sx={{
							textTransform: 'none',
							fontWeight: 600,
							borderRadius: 6,
							py: 0.75,
							px: 2,
							minHeight: 38,
							borderColor: '#e2e8f0',
						color: selected ? '#fff' : '#1e293b',
						background: selected ? '#374151' : '#fff',
						boxShadow: 'none',
						'&:hover': {
							background: selected ? '#374151' : 'rgba(0,29,56,0.04)',
							borderColor: selected ? '#374151' : Brand.molecularBlue,
								boxShadow: 'none',
							},
							'&.Mui-disabled': { opacity: 0.4 },
						}}
					>
						{t.label}
					</Button>
				);
			})}
		</Box>
	);
}

/** True when the user has started this step (used for section reveal logic). */
function stepHasSelections(stepId, v) {
	switch (stepId) {
		case 'product':
			return Boolean(v.productFamily);
		case 'services':
			return v.statsServices === 'yes' || v.statsServices === 'no';
		case 'therapeutic':
			return Array.isArray(v.therapeuticAreas) && v.therapeuticAreas.length > 0;
		case 'goal':
			return Boolean(v.studyGoal);
		case 'scope': {
			const lines = Array.isArray(v.sampleLines) ? v.sampleLines : [];
			return lines.some(
				(ln) =>
					String(ln?.count ?? '').trim() ||
					ln?.species ||
					ln?.matrix ||
					String(ln?.otherNotes ?? '').trim()
			);
		}
		default:
			return false;
	}
}

export function ProductFitForm({ value, onChange, onContinue }) {
	const [finderOpen, setFinderOpen] = useState(false);
	const [showSubmitValidation, setShowSubmitValidation] = useState(false);
	const line0Species = value.sampleLines?.[0]?.species;
	const matrixCategoriesLine0 = useMemo(
		() => (line0Species ? (MATRIX_OPTIONS_BY_SPECIES[line0Species] || []) : []),
		[line0Species]
	);

	const productOptions = useMemo(
		() => PRODUCT_FAMILIES.map((p) => ({ ...p, icon: PRODUCT_FAMILY_ICONS[p.id] })),
		[]
	);

	const studyGoalOptions = useMemo(
		() =>
			STUDY_GOALS.map((g) => ({
				id: g.id,
				label: g.label,
				icon: STUDY_GOAL_ICONS[g.id],
				tileGradient: 'linear-gradient(135deg, #284A6F 0%, #855DA3 100%)',
			})),
		[]
	);

	const matrixTileOptionsLine0 = useMemo(
		() =>
			matrixCategoriesLine0.map((m, i) => ({
				id: m,
				label: m,
				tileGradient: FALLBACK_SPECIES_GRADIENTS[i % FALLBACK_SPECIES_GRADIENTS.length],
			})),
		[matrixCategoriesLine0]
	);

	const matrixCategoryTiles = useMemo(
		() => MATRIX_CATEGORIES.map((c, i) => ({
			id: c,
			label: c,
			tileGradient: FALLBACK_SPECIES_GRADIENTS[i % FALLBACK_SPECIES_GRADIENTS.length],
		})),
		[]
	);

	const line0MatrixCategory = value.sampleLines?.[0]?.matrixCategory;
	const matrixTypeTilesLine0 = useMemo(
		() => (line0MatrixCategory ? (MATRIX_TYPES_BY_CATEGORY[line0MatrixCategory] || []).map((t, i) => ({
			id: t,
			label: t,
			tileGradient: FALLBACK_SPECIES_GRADIENTS[i % FALLBACK_SPECIES_GRADIENTS.length],
		})) : []),
		[line0MatrixCategory]
	);

	const scopeSampleTotal = useMemo(
		() => (value.sampleLines || []).reduce((s, ln) => s + (parseInt(String(ln.count), 10) || 0), 0),
		[value.sampleLines]
	);

	const speciesTiles = useMemo(
		() => COMMON_SPECIES_TILES.map((sp, i) => ({
			id: sp,
			label: sp,
			tileGradient: FALLBACK_SPECIES_GRADIENTS[i % FALLBACK_SPECIES_GRADIENTS.length],
		})),
		[]
	);

	const setField = (field, newVal) => {
		const next = { ...value, [field]: newVal };
		if (field === 'studyGoal' && newVal !== 'other') next.studyGoalOther = '';
		if (field === 'therapeuticAreas') {
			if (!newVal?.includes('other-therapeutic')) next.therapeuticAreaOther = '';
		}
		if (field === 'statsServices') {
			if (newVal !== 'yes') next.interpretationLevel = null;
		}

		const initial = getInitialStudyBuilderSelections();
		const s1 = Boolean(next.productFamily);
		const s2 = s1 && isServicesStepComplete(next);
		const s3 = s2 && isTherapeuticStepComplete(next);
		const s4 = s3 && isGoalStepComplete(next);

		if (!s1) {
			next.statsServices = initial.statsServices;
			next.interpretationLevel = initial.interpretationLevel;
		}
		if (!s2) {
			next.therapeuticAreas = initial.therapeuticAreas;
			next.therapeuticAreaOther = initial.therapeuticAreaOther;
		}
		if (!s3) {
			next.studyGoal = initial.studyGoal;
			next.studyGoalOther = initial.studyGoalOther;
		}
		if (!s4) {
			next.matrixScopeMode = initial.matrixScopeMode;
			next.sampleLines = initial.sampleLines;
		}

		onChange(next);
	};

	const setMatrixScopeMode = (mode) => {
		const first = (value.sampleLines || [])[0] || { id: 'sl-1', count: '', species: null, matrix: null, matrixCategory: null, matrixType: null, otherNotes: '' };
		let lines = [{ ...first, id: first.id || 'sl-1' }];
		if (mode === 'unsure' || mode === 'multiple') {
			lines[0] = { ...lines[0], species: null, matrix: null, matrixCategory: null, matrixType: null };
		}
		onChange({
			...value,
			matrixScopeMode: mode,
			numberOfMatrices: null,
			sampleLines: lines,
		});
	};

	const updateSampleLine = (idx, patch) => {
		const lines = [...(value.sampleLines || [])];
		const prev = lines[idx] || {};
		let next = { ...prev, ...patch };
		if ('species' in patch && patch.species !== prev.species) {
			next = { ...next, matrix: null, matrixCategory: null, matrixType: null };
			if (patch.species !== 'Other') next.otherNotes = '';
		}
		if ('matrix' in patch && patch.matrix !== prev.matrix) {
			next = { ...next, matrixCategory: null, matrixType: null };
		}
		if ('matrixCategory' in patch && patch.matrixCategory !== prev.matrixCategory) {
			next = { ...next, matrixType: null };
		}
		lines[idx] = next;
		onChange({ ...value, sampleLines: lines });
	};


	const complete = isFitComplete(value);
	const showFitBlockingDetails = !complete && showSubmitValidation;

	useEffect(() => {
		if (complete) setShowSubmitValidation(false);
	}, [complete]);

	const scopeLine0CountError = showFitBlockingDetails ? getSampleCountInputError(value.sampleLines?.[0]?.count) : null;

	const sec1Valid = Boolean(value.productFamily);
	const sec2Valid = sec1Valid && isServicesStepComplete(value);
	const sec3Valid = sec2Valid && isTherapeuticStepComplete(value);
	const sec4Valid = sec3Valid && isGoalStepComplete(value);

	const sectionVisible = {
		product: true,
		services: sec1Valid,
		therapeutic: sec2Valid,
		goal: sec3Valid,
		scope: sec4Valid,
	};
	const multiUnsureComplete = (() => {
		if (value.matrixScopeMode !== 'multiple' && value.matrixScopeMode !== 'unsure') return true;
		const numMatrices = parseInt(String(value.numberOfMatrices ?? '').replace(/,/g, ''), 10);
		const count = parseInt(String(value.sampleLines?.[0]?.count ?? '').replace(/,/g, ''), 10);
		const notes = String(value.sampleLines?.[0]?.otherNotes || '').trim();
		return Number.isFinite(numMatrices) && numMatrices >= 1 && Number.isFinite(count) && count >= 1 && notes.length >= 8;
	})();
	const showContinueCta = sec4Valid && stepHasSelections('scope', value) && multiUnsureComplete;


	const handleClearAllSelections = () => {
		if (!window.confirm('Clear all selections and start over?')) return;
		onChange(getInitialStudyBuilderSelections());
		setShowSubmitValidation(false);
	};

	return (
		<Box data-testid="product-fit-form" sx={{ maxWidth: 740, mx: 'auto' }}>
			<ProductFinderDialog
				open={finderOpen}
				onClose={() => setFinderOpen(false)}
				onApplyFamily={(id) => {
					setField('productFamily', id);
				}}
			/>

			{/* Page intro */}
			<Box sx={{ mb: 6, pt: 2, textAlign: 'center' }}>
				<Typography
					variant="h3"
					component="h1"
					sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', mb: 1.5, fontSize: { xs: '1.75rem', sm: '2.25rem' } }}
				>
					{PRODUCT_FIT_INTRO.title}
				</Typography>
				<Typography variant="body1" sx={{ color: '#64748b', lineHeight: 1.75, maxWidth: 520, mx: 'auto', fontSize: '1.05rem' }}>
					{PRODUCT_FIT_INTRO.subtitle}
				</Typography>
			</Box>

			{/* Project name */}
			<Box sx={{ mb: 5 }}>
				<Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1, textAlign: 'center', letterSpacing: '-0.01em', fontSize: '1.35rem' }}>
					Name of Project
				</Typography>
				<TextField
					fullWidth
					placeholder="e.g. Phase II Biomarker Study"
					value={value.projectName || ''}
					onChange={(e) => setField('projectName', e.target.value)}
					inputProps={{ 'data-testid': 'project-name-input' }}
					sx={{
						maxWidth: 480,
						'& .MuiOutlinedInput-root': { borderRadius: 2 },
					}}
				/>
			</Box>

			{/* Section 1: Product Line */}
			<FitSection
				id="product"
				visible={sectionVisible.product}
				title={FIT_STEPS[0].title}
				subtitle={FIT_STEPS[0].subtitle}
			>
				<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<AutoAwesomeOutlined />}
						onClick={() => setFinderOpen(true)}
						sx={{
							borderColor: '#e2e8f0',
							color: '#475569',
							fontWeight: 600,
							textTransform: 'none',
							borderRadius: 6,
							px: 2,
							'&:hover': { borderColor: Brand.molecularBlue, background: 'rgba(40, 74, 111, 0.04)' },
						}}
					>
						Help me choose
					</Button>
				</Box>
				<ChoiceLogoTiles
					options={productOptions}
					value={value.productFamily}
					onChange={(id) => setField('productFamily', id)}
					columns={{ xs: 12, sm: 6, md: 4 }}
					namePrefix="product-family"
				/>
			</FitSection>

			{/* Section 2: Services & Interpretation */}
			<FitSection
				id="services"
				visible={sectionVisible.services}
				title={FIT_STEPS[1].title}
				subtitle={FIT_STEPS[1].subtitle}
			>
				<Paper
					elevation={0}
					sx={{
						p: 2.5,
						mb: 3.5,
						borderRadius: 3,
						background: '#f8fafc',
						border: '1px solid #e2e8f0',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						textAlign: 'center',
					}}
				>
					<Avatar
						sx={{
							width: 48,
							height: 48,
							mb: 1,
							background: Brand.darkModeBlue,
							color: Brand.pureWhite,
						}}
					>
						<DatasetOutlined />
					</Avatar>
					<Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', letterSpacing: 1 }}>
						Always Included
					</Typography>
					<Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mt: 0.5 }}>
						{SELF_SERVICE_BASE.label}
					</Typography>
					<Typography variant="body2" sx={{ color: '#64748b', mt: 1, lineHeight: 1.6, maxWidth: 560 }}>
						{SELF_SERVICE_BASE.description}
					</Typography>
				</Paper>

				<Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5, textAlign: 'center' }}>
					Do you want statistics services on this study?
				</Typography>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, justifyContent: 'center' }}>
					<ServicePickCard
						testId="stats-services-yes"
						selected={value.statsServices === 'yes'}
						title="Yes \u2014 include statistics"
						body="Biostatistics support for design, analysis, and reporting on this study."
						icon={<BarChart sx={{ fontSize: 28 }} />}
						stripeGradient={`linear-gradient(90deg, ${Brand.livingPurple}, ${Brand.molecularBlue})`}
						onClick={() => setField('statsServices', value.statsServices === 'yes' ? null : 'yes')}
					/>
					<ServicePickCard
						testId="stats-services-no"
						selected={value.statsServices === 'no'}
						title="No \u2014 data only"
						body="Measurements and deliverables without a statistics services package."
						icon={<Speed sx={{ fontSize: 28 }} />}
						stripeGradient={'linear-gradient(90deg, #e2e8f0, #9facc6)'}
						onClick={() => setField('statsServices', value.statsServices === 'no' ? null : 'no')}
					/>
				</Box>

				{value.statsServices === 'yes' && (
					<Box>
						<Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5, textAlign: 'center' }}>
							Interpretation level
						</Typography>
						<Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2, textAlign: 'center' }}>
							Choose one - statistics can still be "yes" without an interpretation package.
						</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
							<ServicePickCard
								testId="interpretation-none"
								selected={value.interpretationLevel === 'none'}
								title="No interpretation"
								body="Statistics support only - no dedicated interpretation deliverable on this study."
								icon={<NotInterestedOutlined sx={{ fontSize: 28 }} />}
								stripeGradient={'linear-gradient(90deg, #e2e8f0, #b8c4d6)'}
							onClick={() => setField('interpretationLevel', value.interpretationLevel === 'none' ? null : 'none')}
						/>
						<ServicePickCard
							testId="interpretation-standard"
							selected={value.interpretationLevel === 'standard'}
							title="Standard interpretation"
							body="Expert review with clear, publication-ready reporting."
							icon={<MenuBook sx={{ fontSize: 28 }} />}
							stripeGradient={`linear-gradient(90deg, ${Brand.molecularBlue}, #3987ef)`}
							onClick={() => setField('interpretationLevel', value.interpretationLevel === 'standard' ? null : 'standard')}
						/>
						<ServicePickCard
							testId="interpretation-advanced"
							selected={value.interpretationLevel === 'advanced'}
							title="Advanced interpretation"
							body="Deeper narrative, pathways, and extended synthesis for complex programs."
							icon={<AutoStories sx={{ fontSize: 28 }} />}
							stripeGradient={`linear-gradient(90deg, ${Brand.activatedRed}, ${Brand.insightfulOrange})`}
							onClick={() => setField('interpretationLevel', value.interpretationLevel === 'advanced' ? null : 'advanced')}
						/>
						</Box>
					</Box>
				)}
			</FitSection>

			{/* Section 3: Therapeutic Areas */}
			<FitSection
				id="therapeutic"
				visible={sectionVisible.therapeutic}
				title={FIT_STEPS[2].title}
				subtitle={FIT_STEPS[2].subtitle}
			>
				<TherapeuticAreaTileGrid
					selectedIds={value.therapeuticAreas || []}
					onChange={(ids) => setField('therapeuticAreas', ids)}
					max={3}
				/>
				{(value.therapeuticAreas || []).includes('other-therapeutic') && (
					<TextField
						fullWidth
						size="small"
						sx={{ mt: 2 }}
						label="Describe \u201cother\u201d therapeutic focus"
						value={value.therapeuticAreaOther || ''}
						onChange={(e) => setField('therapeuticAreaOther', e.target.value)}
						inputProps={{ 'data-testid': 'therapeutic-area-other' }}
					/>
				)}
			</FitSection>

			{/* Section 4: Study Goal */}
			<FitSection
				id="goal"
				visible={sectionVisible.goal}
				title={FIT_STEPS[3].title}
				subtitle={FIT_STEPS[3].subtitle}
			>
				<ChoiceLogoTiles
					options={studyGoalOptions}
					value={value.studyGoal}
					onChange={(id) => setField('studyGoal', id)}
					columns={{ xs: 6, sm: 4, md: 3 }}
					sizePreset="sm"
					namePrefix="study-goal"
				/>
				{value.studyGoal === 'other' && (
					<TextField
						fullWidth
						size="small"
						sx={{ mt: 2 }}
						label="Describe your goal"
						value={value.studyGoalOther || ''}
						onChange={(e) => setField('studyGoalOther', e.target.value)}
						inputProps={{ 'data-testid': 'study-goal-other' }}
					/>
				)}
			</FitSection>

			{/* Section 5: Scope & Samples */}
			<FitSection
				id="scope"
				visible={sectionVisible.scope}
				title={FIT_STEPS[4].title}
				subtitle={FIT_STEPS[4].subtitle}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>
					How do you want to describe matrices / tissues?
				</Typography>
				<ToggleButtonGroup
					exclusive
					fullWidth
					size="medium"
					value={value.matrixScopeMode || 'single'}
					onChange={(e, next) => {
						if (next != null) setMatrixScopeMode(next);
					}}
					sx={{
						mb: 2,
						flexWrap: 'wrap',
						gap: 0.75,
						'& .MuiToggleButton-root': {
							flex: '1 1 118px',
							fontWeight: 600,
							textTransform: 'none',
							py: 1.25,
							minHeight: 48,
							lineHeight: 1.25,
							borderRadius: '12px !important',
							borderColor: '#e2e8f0',
							'&.Mui-selected': {
								background: Brand.darkModeBlue,
								color: '#fff',
								borderColor: Brand.darkModeBlue,
								'&:hover': { background: Brand.darkModeBlue },
							},
						},
					}}
				>
					<ToggleButton value="single" data-testid="matrix-scope-single">
						Single matrix
					</ToggleButton>
					<ToggleButton value="multiple" data-testid="matrix-scope-multiple">
						Multiple matrices
					</ToggleButton>
					<ToggleButton value="unsure" data-testid="matrix-scope-unsure">
						Unsure
					</ToggleButton>
				</ToggleButtonGroup>
				<Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2, maxWidth: 640, lineHeight: 1.5 }}>
					Single: one species and matrix. Multiple: one row per tissue so sample totals add up. Unsure: approximate sample count plus a
					short note \u2014 we will help you scope.
				</Typography>

				{(value.matrixScopeMode || 'single') === 'single' && (
					<Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e2e8f0' }}>
						<TextField
							fullWidth
							margin="normal"
							type="number"
							label="Number of samples"
							value={value.sampleLines?.[0]?.count ?? ''}
							onChange={(e) => updateSampleLine(0, { count: e.target.value })}
							inputProps={{ min: 1, 'data-testid': 'scope-sample-count' }}
							error={Boolean(scopeLine0CountError)}
							helperText={scopeLine0CountError || undefined}
							sx={{ ...SCOPE_FIELD_SX, maxWidth: { xs: '100%', sm: 320 }, mb: 1 }}
						/>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5, mt: 2 }}>
							Species
						</Typography>
						<ChoiceLogoTiles
							options={speciesTiles}
							value={value.sampleLines?.[0]?.species}
							onChange={(id) => updateSampleLine(0, { species: id })}
							columns={{ xs: 6, sm: 4, md: 3 }}
							sizePreset="md"
							namePrefix="scope-species"
						/>
						{value.sampleLines?.[0]?.species === 'Other' && (
							<TextField
								fullWidth
								margin="normal"
								multiline
								minRows={2}
								label="Describe species and matrix"
								value={value.sampleLines?.[0]?.otherNotes || ''}
								onChange={(e) => updateSampleLine(0, { otherNotes: e.target.value })}
								inputProps={{ 'data-testid': 'matrix-other-details' }}
								InputLabelProps={{ shrink: true }}
								sx={{ mt: 4.25, ...SCOPE_FIELD_SX, '& .MuiOutlinedInput-root': { alignItems: 'flex-start', minHeight: 88 } }}
							/>
						)}
						{value.sampleLines?.[0]?.species && value.sampleLines[0].species !== 'Other' && (
							<Box sx={{ mt: 2.5 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
									Organism
								</Typography>
								{matrixTileOptionsLine0.length > 0 ? (
									<ChoiceLogoTiles
										options={matrixTileOptionsLine0}
										value={value.sampleLines[0].matrix}
										onChange={(id) => updateSampleLine(0, { matrix: id })}
										columns={{ xs: 6, sm: 4, md: 3 }}
										sizePreset="md"
										namePrefix="scope-matrix"
									/>
								) : (
									<TextField
										fullWidth
										label="Organism"
										value={value.sampleLines[0].matrix || ''}
										onChange={(e) => updateSampleLine(0, { matrix: e.target.value })}
										inputProps={{ 'data-testid': 'scope-matrix-text' }}
										sx={SCOPE_FIELD_SX}
									/>
								)}
							</Box>
						)}
						{value.sampleLines?.[0]?.matrix && value.sampleLines[0].species !== 'Other' && (
							<Box sx={{ mt: 2.5 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
									Matrix
								</Typography>
								<ChoiceLogoTiles
									options={matrixCategoryTiles}
									value={value.sampleLines[0].matrixCategory}
									onChange={(id) => updateSampleLine(0, { matrixCategory: id })}
									columns={{ xs: 6, sm: 4, md: 3 }}
									sizePreset="md"
									namePrefix="scope-matrix-category"
								/>
							</Box>
						)}
						{value.sampleLines?.[0]?.matrixCategory && (
							<Box sx={{ mt: 2.5 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
									Matrix Type
								</Typography>
								<ChoiceLogoTiles
									options={matrixTypeTilesLine0}
									value={value.sampleLines[0].matrixType}
									onChange={(id) => updateSampleLine(0, { matrixType: id })}
									columns={{ xs: 6, sm: 4, md: 3 }}
									sizePreset="md"
									namePrefix="scope-matrix-type"
								/>
							</Box>
						)}
					</Box>
				)}

				{(value.matrixScopeMode === 'unsure' || value.matrixScopeMode === 'multiple') && (() => {
					const numVal = String(value.numberOfMatrices ?? '').trim();
					const countVal = String(value.sampleLines?.[0]?.count ?? '').trim();
					const notesVal = String(value.sampleLines?.[0]?.otherNotes || '').trim();
					const anyFilled = Boolean(numVal || countVal || notesVal);
					const numMissing = anyFilled && !numVal;
					const countMissing = anyFilled && !countVal;
					const notesMissing = anyFilled && notesVal.length < 8;
					return (
						<Box sx={{ mt: 2, pt: 3, borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 3 }}>
							<Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
								<TextField
									type="number"
									label="Number of matrices"
									value={value.numberOfMatrices ?? ''}
									onChange={(e) => onChange({ ...value, numberOfMatrices: e.target.value })}
									inputProps={{ min: 1, 'data-testid': 'scope-number-of-matrices' }}
									InputLabelProps={{ shrink: true }}
									error={numMissing}
									helperText={numMissing ? 'Enter the number of matrices' : undefined}
									sx={{ ...SCOPE_FIELD_SX, flex: 1, minWidth: 180 }}
								/>
								<TextField
									type="number"
									label="Approximate number of samples"
									value={value.sampleLines?.[0]?.count ?? ''}
									onChange={(e) => updateSampleLine(0, { count: e.target.value })}
									inputProps={{ min: 1, 'data-testid': 'scope-sample-count' }}
									InputLabelProps={{ shrink: true }}
									error={countMissing}
									helperText={countMissing ? 'Enter an approximate number of samples' : undefined}
									sx={{ ...SCOPE_FIELD_SX, flex: 1, minWidth: 180 }}
								/>
							</Box>
							<TextField
								fullWidth
								multiline
								minRows={4}
								label="What should we know?"
								placeholder="e.g. mix of plasma and tissue, species not finalized, pilot vs full cohort\u2026"
								value={value.sampleLines?.[0]?.otherNotes || ''}
								onChange={(e) => updateSampleLine(0, { otherNotes: e.target.value })}
								inputProps={{ 'data-testid': 'scope-unsure-notes' }}
								error={notesMissing}
								helperText={notesMissing ? 'Tell us what we should know (at least 8 characters)' : undefined}
								sx={{
									...SCOPE_FIELD_SX,
									'& .MuiOutlinedInput-root': { alignItems: 'flex-start', minHeight: 120 },
									'& .MuiOutlinedInput-input': { py: 1.5 },
								}}
							/>
						</Box>
					);
				})()}

				<Paper
					variant="outlined"
					sx={{ mt: 3, p: 2.5, borderRadius: 3, borderColor: '#e2e8f0', background: '#f8fafc' }}
				>
					<Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
						Total samples (all lines): {scopeSampleTotal}
					</Typography>
					<Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5, lineHeight: 1.5 }}>
						This total carries into logistics and your quote estimate.
					</Typography>
				</Paper>

				{showFitBlockingDetails && (
					<Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
						<Alert
							severity="warning"
							icon={false}
							data-testid="fit-incomplete-reasons"
							sx={{
								maxWidth: 720,
								width: '100%',
								py: 2,
								px: { xs: 2, sm: 3 },
								justifyContent: 'center',
								'& .MuiAlert-message': { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 0 },
							}}
						>
							<Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, textAlign: 'center', width: '100%' }}>
								Finish the items below to get your estimate
							</Typography>
							<Box
								component="ul"
								sx={{ m: 0, pl: 2.25, pr: 0, mx: 'auto', width: 'fit-content', maxWidth: '100%', textAlign: 'left', boxSizing: 'border-box' }}
							>
								{getFitIncompleteReasons(value).map((reason, ri) => (
									<Typography component="li" variant="body2" key={`fit-reason-${ri}`} sx={{ mb: 0.5, lineHeight: 1.55 }}>
										{String(reason)}
									</Typography>
								))}
							</Box>
						</Alert>
					</Box>
				)}
			</FitSection>

			{/* Bottom actions */}
			{showContinueCta && (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2, pb: 4 }}>
					<Button
						data-testid="continue-to-quote"
						variant="contained"
						size="large"
						onClick={() => {
							if (complete) {
								onContinue();
								return;
							}
							setShowSubmitValidation(true);
						}}
						sx={{
							background: Brand.darkModeBlue,
							fontWeight: 700,
							px: 5,
							py: 1.75,
							fontSize: '1rem',
							borderRadius: 3,
							width: { xs: '100%', sm: 'auto' },
							boxShadow: '0 4px 14px rgba(0, 29, 56, 0.2)',
							'&:hover': { background: '#1a3a5c', boxShadow: '0 6px 20px rgba(0, 29, 56, 0.25)' },
						}}
					>
						Build this study
					</Button>
					<Button
						data-testid="fit-clear-all-selections"
						type="button"
						variant="text"
						onClick={handleClearAllSelections}
						sx={{ fontWeight: 500, textTransform: 'none', color: '#94a3b8', fontSize: '0.85rem' }}
					>
						Clear all selections
					</Button>
				</Box>
			)}
		</Box>
	);
}
